import axios, { AxiosInstance } from "axios";
import { getRefreshedUserConfig } from "./authHelper";
import { globalDispatch } from "../aplUtils/aplGlobals";
import { clearSession, setUserConfigRedux } from "../redux/slices/auth.feature";
import { getAplTokens, removeAplTokens, setAplTokensRefreshing, updateAplTokens } from "../aplUtils/cookiesWrapper";
import { waitForValue } from "../aplUtils/waitForValue";

type IRequestCb = (token: string) => void;
let refreshSubscribers: IRequestCb[] = [];

function onRefreshed({ authorisationToken }: { authorisationToken: string }) {
    refreshSubscribers.map(cb => cb(authorisationToken));
    refreshSubscribers = [];
}

const subscribeTokenRefresh = (cb: IRequestCb) => {
    refreshSubscribers.push(cb);
};

const aplClient = (): AxiosInstance => {
    let customAxiosClient = axios.create({
        baseURL: "/api",
        headers: {
            "Content-type": "application/json"
        },
        withCredentials: true
    });

    customAxiosClient.interceptors.request.use(
        async config => {
            let currentTokens = getAplTokens();
            if (currentTokens.token) {
                config.headers.Authorization = ` bearer ${currentTokens.token}`;
            }
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );

    customAxiosClient.interceptors.response.use(
        response => {
            return response;
        },
        async function (error) {
            const originalRequest = error.config;

            if (error.response?.status === 401 && error.response.headers.has("token-expired")) {
                if (getAplTokens()?.refreshToken) {
                    originalRequest._retry = true;

                    const retryOrigReq = new Promise(resolve => {
                        subscribeTokenRefresh((newToken: string) => {
                            if (originalRequest.headers) {
                                originalRequest.headers.authorization = `Bearer ${newToken}`;
                            }
                            resolve(axios(originalRequest));
                        });
                    });

                    if (getAplTokens().refreshing) {
                        await waitForValue(getAplTokens().refreshing, false, 5000);
                        let newTokens = getAplTokens();
                        if (originalRequest.headers) {
                            originalRequest.headers.authorization = `Bearer ${newTokens.token}`;
                        }

                        onRefreshed({
                            authorisationToken: newTokens.token
                        });

                        return retryOrigReq;
                    } else {
                        setAplTokensRefreshing();
                        getRefreshedUserConfig(getAplTokens())
                            .then(resp => {
                                updateAplTokens(resp.data.token, resp.data.refreshToken);
                                let tokens = getAplTokens();
                                if(tokens.refreshToken !== resp.data.refreshToken) {
                                    // throw new Error("Saved refreshToken mismatch");
                                    console.log(`Saved refreshToken mismatch new=${resp.data.refreshToken} cookie=${tokens.refreshToken}`);
                                }

                                globalDispatch(setUserConfigRedux(resp.data));

                                if (originalRequest.headers) {
                                    originalRequest.headers.authorization = `Bearer ${resp.data.token}`;
                                }

                                onRefreshed({
                                    authorisationToken: resp.data.token
                                });
                            })
                            .catch(e => {
                                if (e.response.headers.has("refresh-token-used") || e.response.headers.has("refresh-token-revoked")) {
                                    console.log(`${new Date().toISOString()} error refreshing token. refresh-token-used=${e.response.headers["refresh-token-used"]} refresh-token-revoked=${e.response.headers["refresh-token-revoked"]}`);
                                    if (globalDispatch) globalDispatch(clearSession());
                                    removeAplTokens();
                                }
                                else {
                                    console.log(`${new Date().toISOString()} error refreshing token ${e}`);
                                }
                            })
                            .finally(() => {
                                // isRefreshing = false;
                            });
                    }
                    return retryOrigReq;
                } else {
                    return Promise.reject(error);
                }
            }
            return Promise.reject(error);
        }
    );

    return customAxiosClient;
};

export { aplClient };
